import React, { useState } from "react";
import { Button, Card, Col, Container, Form, Row, Alert, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { 
  Envelope, 
  ArrowLeft, 
  CheckCircle, 
  Send, 
  ShieldCheck,
  Key,
  Lock
} from "react-bootstrap-icons";
import axios from "axios";
import bg1 from "../assets/img/imagecover.png";
import logo from "../assets/img/logo1.png";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");

  // Supprimer la classe sidebar-show
  document.body.classList.remove("sidebar-show");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    // Validation simple
    if (!email) {
      setError("Veuillez saisir votre adresse email");
      setLoading(false);
      return;
    }

   try {
  const response = await fetch(`${API_BASE_URL}/bo/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ email: email })
  });

  const data = await response.json();

  // Gérer différents formats de réponse
  if (response.ok) {
    // Vérifier si le succès est indiqué de différentes manières
    const isSuccess = 
      data.success === 200 || 
      data.success === true || 
      data.code === 200 ||
      data.status === 'success';
    
    if (isSuccess) {
      setSuccess(true);
      setDebugInfo(data.message || "Un email de réinitialisation a été envoyé");
    } else {
      setError(data.message || "Aucun compte trouvé avec cette adresse email");
    }
  } else {
    // Gérer les erreurs HTTP (400, 404, 500, etc.)
    setError(data.message || `Erreur ${response.status}: Demande impossible`);
  }
} catch (err) {
  setError(`Erreur réseau : ${err.message}`);
  console.error('Erreur de connexion:', err);
} finally {
  setLoading(false);
}
  };

  return (
    <div className="page-sign d-block py-0" style={styles.page}>
      <Row className="g-0 min-vh-100">
        {/* Colonne gauche - Image héros */}
        <Col md="7" lg="8" xl="9" className="d-none d-lg-block">
          <div style={{
            ...styles.imageContainer,
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.5)), url(${bg1})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}>
            <div style={styles.forgotHeroContent}>
              <div style={styles.forgotHeroIcon}>
                <Key style={styles.forgotHeroIconSvg} />
              </div>
              <h2 style={styles.loginHeroTitle}>
                Mot de passe oublié ?
              </h2>
              <p style={styles.loginHeroSubtitle}>
                Ne vous inquiétez pas, nous allons vous aider à réinitialiser votre mot de passe
              </p>
              
              <div style={styles.featuresList}>
                <div style={styles.featureItemLogin}>
                  <ShieldCheck style={styles.featureIconLogin} />
                  <span>Réinitialisation sécurisée</span>
                </div>
                <div style={styles.featureItemLogin}>
                  <Send style={styles.featureIconLogin} />
                  <span>Lien envoyé par email</span>
                </div>
                <div style={styles.featureItemLogin}>
                  <Lock style={styles.featureIconLogin} />
                  <span>Protection des données</span>
                </div>
              </div>
            </div>
          </div>
        </Col>
        
        {/* Colonne droite - Formulaire */}
        <Col md="5" lg="4" xl="3" className="col-wrapper">
          <Card className="card-sign" style={styles.loginCard}>
            <Card.Header style={styles.loginCardHeader}>
              <Button 
                variant="link" 
                style={styles.backButton}
                onClick={() => navigate("/login")}
              >
                <ArrowLeft style={{marginRight: '6px'}} />
                Retour à la connexion
              </Button>
              <div style={styles.loginHeaderContent}>
                <div style={{
                  ...styles.userTypeIndicatorCompact,
                  backgroundColor: '#3498db'
                }}>
                  <Key />
                </div>
                <div>
                  <Card.Title style={styles.loginTitle}>
                    Mot de passe oublié
                  </Card.Title>
                  <Card.Text style={styles.loginSubtitle}>
                    Entrez votre email pour réinitialiser votre mot de passe
                  </Card.Text>
                </div>
              </div>
            </Card.Header>
            
            <Card.Body style={styles.loginCardBody}>
              <br />
              
              {/* Messages d'erreur */}
              {error && (
                <Alert variant="danger" style={styles.alertMessage}>
                  <Alert.Heading style={styles.alertHeading}>Erreur</Alert.Heading>
                  <div style={{ fontSize: '0.9rem' }}>
                    {error}
                  </div>
                </Alert>
              )}
              
              {/* Message de succès */}
              {success && (
                <Alert 
                  variant="success" 
                  className="text-center border-success shadow-sm"
                  style={{
                    ...styles.alertMessage,
                    borderLeft: '4px solid #198754',
                    animation: 'fadeIn 0.5s ease-out'
                  }}
                >
                  <div className="d-flex flex-column align-items-center">
                    <CheckCircle 
                      className="mb-3" 
                      style={{ 
                        ...styles.successIcon, 
                        fontSize: '3rem',
                        animation: 'scaleIn 0.3s ease-out 0.1s both'
                      }} 
                    />
                    <Alert.Heading className="h5 fw-bold mb-2" style={styles.alertHeading}>
                      Email envoyé !
                    </Alert.Heading>
                    <p className="text-muted mb-0" style={styles.alertText}>
                      {debugInfo || "Un lien de réinitialisation vous a été envoyé par email."}
                    </p>
                    <Button 
                      variant="primary" 
                      className="mt-3"
                      onClick={() => navigate("/login")}
                      style={styles.successButton}
                    >
                      Retour à la connexion
                    </Button>
                  </div>
                </Alert>
              )}
              
              {!success && (
                <Form onSubmit={handleSubmit}>
                  <div style={styles.formGroup}>
                    <Form.Label style={styles.formLabel}>
                      <Envelope style={styles.inputIcon} />
                      Adresse email
                    </Form.Label>
                    <Form.Control 
                      type="email" 
                      placeholder="exemple@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      style={styles.formInput}
                    />
                    <Form.Text style={styles.formText}>
                      Nous vous enverrons un lien pour réinitialiser votre mot de passe
                    </Form.Text>
                  </div>
                  
                  <Button 
                    type="submit" 
                    variant="primary"
                    className="w-100"
                    disabled={loading}
                    style={{
                      ...styles.loginButton,
                      background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="me-2" />
                        Envoyer le lien
                      </>
                    )}
                  </Button>
                </Form>
              )}
            </Card.Body>
            
            <Card.Footer style={styles.loginCardFooter}>
              <div style={styles.securityInfo}>
                <Lock style={styles.securityIconFooter} />
                <span style={styles.securityTextFooter}>Réinitialisation sécurisée SSL/TLS</span>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// Styles identiques à ceux de Signin2.js avec quelques ajouts
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f4f8',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },

  imageContainer: {
    height: '100%',
    minHeight: '100vh',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
  },

  forgotHeroContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth: '600px',
    padding: '2rem',
    textAlign: 'center',
    color: 'white',
  },

  forgotHeroIcon: {
    fontSize: '4rem',
    marginBottom: '1.5rem',
    color: '#ffffff',
  },

  forgotHeroIconSvg: {
    fontSize: '4rem',
    color: '#f1c40f',
  },

  loginHeroTitle: {
    fontSize: '2.5rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: '#ffffff',
  },

  loginHeroSubtitle: {
    fontSize: '1.2rem',
    opacity: 0.95,
    marginBottom: '2.5rem',
    color: '#f8fafc',
  },

  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '2rem',
  },

  featureItemLogin: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    fontSize: '1.1rem',
    opacity: 0.95,
    padding: '0.75rem',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '25px',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    width: 'fit-content',
    color: '#ffffff',
  },

  featureIconLogin: {
    fontSize: '1.2rem',
    color: '#f1c40f',
  },

  loginCard: {
    border: 'none',
    borderRadius: '0',
    height: '100vh',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa'
  },

  loginCardHeader: {
    borderBottom: '1px solid #e9ecef',
    padding: '1.5rem 1.5rem 1rem',
    backgroundColor: 'white',
    flexShrink: 0,
  },

  loginCardBody: {
    padding: '1.5rem',
    backgroundColor: 'white',
    flex: 1,
    overflowY: 'auto'
  },

  loginCardFooter: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e9ecef',
    backgroundColor: 'white'
  },

  backButton: {
    color: '#475569',
    textDecoration: 'none',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    marginBottom: '1rem',
  },

  loginHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  userTypeIndicatorCompact: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '1.5rem',
  },

  loginTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: '0.25rem',
  },

  loginSubtitle: {
    fontSize: '0.9rem',
    color: '#475569',
  },

  formGroup: {
    marginBottom: '1.5rem'
  },

  formLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '0.5rem'
  },

  inputIcon: {
    marginRight: '8px',
    fontSize: '16px',
    color: '#64748b',
  },

  formInput: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '0.95rem',
    transition: 'all 0.3s ease',
    width: '100%',
    color: '#0f172a',
  },

  formText: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '0.5rem',
    display: 'block',
  },

  loginButton: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    transition: 'all 0.3s ease',
    marginBottom: '1rem',
    color: '#ffffff',
  },

  alertMessage: {
    borderRadius: '8px',
    marginBottom: '1.5rem',
    border: 'none'
  },

  alertHeading: {
    fontSize: '0.95rem',
    fontWeight: '600',
    marginBottom: '0.25rem',
  },

  alertText: {
    fontSize: '0.85rem',
    marginBottom: 0,
    color: '#1e293b',
  },

  successIcon: {
    fontSize: '2rem',
    color: '#15803d',
    marginTop: '0.5rem'
  },

  successButton: {
    padding: '0.5rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: '500',
    borderRadius: '8px',
  },

  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#475569',
    fontSize: '0.85rem',
  },

  securityIconFooter: {
    fontSize: '1rem',
    color: '#16a34a',
  },

  securityTextFooter: {
    color: '#475569',
  },
};